/* „Okładka" — the pool as a wrapping grid, the add tile, the trash badge and the counter.
 *
 * Lifted out of `CreatePlanDialog.module.css` on 22 September 2026, when the row gained the
 * trainer's own covers and stopped being a row. `.field`, `.label` and `.help` are repeated from
 * there rather than shared: they are the create dialog's own field vocabulary, three declarations
 * each, and promoting them would make a shared module out of something one dialog uses. */

.field {
  display: flex;
  flex-direction: column;
  gap: var(--space-6px);
  min-width: var(--space-0);
  margin: var(--space-0);
  padding: var(--space-0);
  border: 0;
}

.label {
  display: flex;
  gap: var(--space-7px);
  align-items: baseline;
  padding: var(--space-0);
  font-size: var(--fs-label);
  font-weight: var(--fw-bold);
  color: var(--text-2);
}

.help {
  font-size: var(--fs-micro);
  line-height: var(--lh-hint);
  color: var(--text-2);
}

/* The refusal stands whether or not there is one to draw — `1lh` of reserved room, so a failed
 * upload changes ink and never geometry. */
.refusal {
  min-height: 1lh;
  font-size: var(--fs-micro);
  line-height: var(--lh-hint);
  color: var(--danger-ink);
}

/* The counter is a figure, so it is tabular like every other figure in this app. */
.count {
  font-size: var(--fs-caption);
  font-variant-numeric: tabular-nums;
  color: var(--text-3);
}

/* ── The grid ───────────────────────────────────────────────────────────────────────────────── */

/* It WRAPS rather than scrolling sideways: with ten of the trainer's own covers beside the curated
 * set, a scroller hides most of the pool behind a gesture nothing announces. `min-height` keeps one
 * row of tiles reserved from the first frame, so the pending read and the answered one open at the
 * same geometry. */
.covers {
  display: grid;
  grid-template-columns: repeat(auto-fill, var(--w-create-cover));
  gap: var(--space-2);
  min-height: var(--h-create-cover);
}

.cover {
  --tile-ink: transparent;

  position: relative;
  box-sizing: border-box;
  width: var(--w-create-cover);
  height: var(--h-create-cover);
  overflow: hidden;
  border: var(--border-thick) solid var(--tile-ink);
  border-radius: var(--radius-mark);
  box-shadow: var(--shadow-tile-resting);
  cursor: pointer;
}

.coverOn {
  --tile-ink: var(--text);

  box-shadow: none;
}

/* A cover on its way out: still drawn, visibly no longer available. */
.coverGoing {
  cursor: default;
  opacity: 0.4;
}

.coverImage {
  width: 100%;
  height: 100%;
}

/* The automatic option draws the picture it WOULD pick, with the word over it — so „auto" is not an
 * empty frame the trainer has to imagine a plan into. */
.coverAuto {
  position: absolute;
  inset: var(--space-0);
  display: grid;
  place-items: center;
  font-size: var(--fs-caption);
  font-weight: var(--fw-bold);
  color: var(--text-on-photo);
  background: var(--wash-on-photo-strong);
}

.cover:has(input:focus-visible) {
  outline: var(--border-focus) solid var(--text);
  outline-offset: var(--space-1px);
}

/* ── The two controls on the grid ───────────────────────────────────────────────────────────── */

/* The trash sits on the picture it removes, so a pool of ten needs no legend to say which button
 * belongs to which cover. A wash rather than a fill: the accent is a scalpel, and a red badge on
 * every own tile would paint the grid with an act the trainer has not asked for yet. */
.trash {
  position: absolute;
  top: var(--space-1px);
  right: var(--space-1px);
  display: grid;
  place-items: center;
  width: var(--sz-cover-badge);
  height: var(--sz-cover-badge);
  padding: var(--space-0);
  color: var(--text-on-photo);
  background: var(--wash-on-photo-strong);
  border: 0;
  border-radius: var(--radius-mark);
  cursor: pointer;
}

.trash:hover {
  color: var(--danger);
}

.trash[aria-disabled='true'] {
  cursor: default;
  opacity: 0.4;
}

/* The add tile is an outline, not a picture: it is the only tile that is not a cover. */
.add {
  display: grid;
  place-items: center;
  border: var(--border-thick) dashed var(--text-3);
  box-shadow: none;
}

.addOff {
  cursor: default;
  opacity: 0.4;
}

.addMark {
  font-size: var(--fs-h3);
  font-weight: var(--fw-bold);
  line-height: var(--lh-none);
  color: var(--text-2);
}
